import { useEffect, useId, useRef, useState } from 'react';
import {
  AppWindow,
  ChatCircleDots,
  Check,
  CircleNotch,
  Database,
  Globe,
  Graph,
  LinkSimple,
  ListChecks,
  PauseCircle,
  Robot,
  SlidersHorizontal,
  Timer,
  UserCircle,
  Users,
  UsersThree,
  WarningCircle,
  Wrench,
  type Icon,
} from '@phosphor-icons/react';
import { EddyAppIcon } from '~/components/eddy-mark';
import { Button } from '~/components/ui/button';
import { Tip } from '~/components/ui/tip';
import { useAnswerProBuilderConsent } from '~/hooks/mutations/use-pro-builder';
import { useProBuilderStatus, type ProBuilderConsentMode, type ProBuilderStatus } from '~/hooks/queries/use-pro-builder';
import {
  useProBuilderConsent,
  type ProBuilderConsent,
  type ProBuilderConsentLineKind,
} from '~/hooks/queries/use-pro-builder-consents';
import { personFacingMessage } from '~/lib/query-client';
import { cn } from '~/lib/utils';
import {
  PRO_BUILDER_CONSENT_APPROVE,
  PRO_BUILDER_CONSENT_APPROVING,
  PRO_BUILDER_CONSENT_DECLINE,
  PRO_BUILDER_CONSENT_DECLINING,
  PRO_BUILDER_CONSENT_MODE_NEXT_TIME,
  PRO_BUILDER_CONSENT_SEND_FAILED,
  PRO_BUILDER_CONSENT_TIME_TOOLTIP,
  PRO_BUILDER_CONSENT_TITLE,
  PRO_BUILDER_CONSENT_WAIT_NOTE,
} from './pro-builder-copy';
import { consentTimeLeft, consentTitle, noticeParts, splitConsentLines } from './pro-builder-consent-view';
import { ProBuilderConsentModeChoice } from './pro-builder-consent-mode-choice';

/** The icon beside each kind of change. A kind this build does not know yet falls back to `other`'s. */
export const CONSENT_KIND_ICON: Record<ProBuilderConsentLineKind, Icon> = {
  app: AppWindow,
  agent: Robot,
  run: LinkSimple,
  tool: Wrench,
  values: SlidersHorizontal,
  data: Database,
  memory: Graph,
  ask: ChatCircleDots,
  runAs: UserCircle,
  publish: Globe,
  audience: Users,
  notice: UsersThree,
  other: ListChecks,
};

/**
 * FRONT-3108 / FRONT-3176: the one card a Claude Code terminal waits on before
 * it changes the app's agents or access, or publishes a shared app. It sits
 * over the terminal as a spotlight: Claude Code is paused while it waits, so
 * dimming the terminal hides nothing that moves, and the scrim lets clicks and
 * scrolling through to the terminal underneath. Pure: the card, the clock, the
 * answer in flight (`pending`) and the last failure go in; the click comes
 * out. The lines are model-authored, so they render as plain text.
 */
export function ProBuilderConsentCard({
  consent,
  now,
  pending,
  errorMessage,
  onAnswer,
  mode,
}: {
  consent: ProBuilderConsent;
  /** The clock the countdown is read against (ms since epoch). */
  now: number;
  /** The answer being sent, or null. While set, both buttons are disabled; never settled optimistically. */
  pending: 'approve' | 'decline' | null;
  errorMessage: string | null;
  onAnswer: (approved: boolean) => void;
  /** FRONT-3230: the "Next time" choice, shown only when the card offers it (`consent.modeChoice`). */
  mode?: { value: ProBuilderConsentMode | undefined; onChange: (mode: ProBuilderConsentMode) => void };
}) {
  const id = useId();
  const eyebrowId = `${id}-eyebrow`;
  const titleId = `${id}-title`;
  const noteId = `${id}-note`;
  const ref = useTakeFocus();
  const time = consentTimeLeft(consent, now);
  const { changes, notices } = splitConsentLines(consent.lines);
  return (
    <div data-testid="pro-builder-consent-spotlight" className="pointer-events-none absolute inset-0 z-10 flex justify-center p-4 pt-6">
      {/* No backdrop blur: Chrome left the live terminal's lower rows unblurred AND undimmed under it (seen in a browser run). */}
      <div
        aria-hidden
        data-testid="pro-builder-consent-scrim"
        className="absolute inset-0 bg-card/75 motion-safe:animate-in motion-safe:fade-in-0 motion-safe:duration-200"
      />
      <section
        ref={ref}
        role="alertdialog"
        aria-modal="false"
        aria-labelledby={`${eyebrowId} ${titleId}`}
        aria-describedby={noteId}
        tabIndex={-1}
        data-pro-builder-autofocus=""
        data-testid="pro-builder-consent-card"
        className={cn(
          'pointer-events-auto relative flex max-h-full w-full max-w-[600px] flex-col self-start overflow-hidden rounded-[18px] border border-[var(--eddy-blue)]/35 bg-card text-card-foreground outline-none',
          'shadow-[0_0_0_4px_var(--eddy-blue-soft),var(--shadow-lg)]',
          'motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-top-2 motion-safe:duration-200 motion-safe:ease-out',
        )}
      >
        <div className="flex shrink-0 items-start gap-3.5 px-5 pb-4 pt-5">
          <EddyAppIcon size={36} className="shrink-0" />
          <div className="min-w-0 flex-1">
            <p id={eyebrowId} className="text-[11px] font-medium uppercase tracking-[0.06em] text-[var(--eddy-blue-text)]">
              {PRO_BUILDER_CONSENT_TITLE}
            </p>
            <h2
              id={titleId}
              data-testid="pro-builder-consent-heading"
              className="mt-0.5 break-words text-[length:var(--text-lg)] font-semibold leading-tight tracking-[-0.015em] [overflow-wrap:anywhere]"
            >
              {consentTitle(consent.heading)}
            </h2>
          </div>
          {time && (
            <Tip label={PRO_BUILDER_CONSENT_TIME_TOOLTIP}>
              <span
                data-testid="pro-builder-consent-countdown"
                data-last-minute={time.lastMinute ? '' : undefined}
                className={cn(
                  'inline-flex h-7 shrink-0 items-center gap-1.5 rounded-full px-2.5 font-mono text-[length:var(--text-xs)] tabular-nums text-foreground',
                  time.lastMinute ? 'border border-amber-500/30 bg-amber-500/10' : 'bg-[var(--bg-3)]',
                )}
              >
                <Timer aria-hidden size={14} className={time.lastMinute ? 'text-amber-600 dark:text-amber-400' : undefined} />
                {time.label}
              </span>
            </Tip>
          )}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5">
          {changes.length > 0 && (
            <ul className="divide-y divide-border rounded-xl border border-border">
              {changes.map((line, i) => {
                const LineIcon = CONSENT_KIND_ICON[line.kind] ?? CONSENT_KIND_ICON.other;
                return (
                  <li key={i} data-kind={line.kind} className="flex items-start gap-3 px-3.5 py-2.5">
                    <span aria-hidden className="inline-flex size-7 shrink-0 items-center justify-center rounded-lg bg-[var(--bg-3)] text-foreground/80">
                      <LineIcon size={17} />
                    </span>
                    <span className="break-words pt-1 text-[length:var(--text-sm)] leading-snug [overflow-wrap:anywhere]">{line.text}</span>
                  </li>
                );
              })}
            </ul>
          )}
          {notices.map((line, i) => {
            const { lead, rest } = noticeParts(line);
            return (
              <div
                key={i}
                data-testid="pro-builder-consent-notice"
                className="mt-3 flex items-start gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 px-3.5 py-3"
              >
                <UsersThree aria-hidden size={19} className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
                <p className="break-words text-[length:var(--text-sm)] leading-normal text-muted-foreground [overflow-wrap:anywhere]">
                  {lead && <strong className="font-semibold text-foreground">{lead}</strong>}
                  {lead && rest ? ' ' : null}
                  {rest}
                </p>
              </div>
            );
          })}
          {errorMessage && (
            <p
              role="alert"
              data-testid="pro-builder-consent-error"
              className="mt-3 flex items-center gap-2.5 rounded-xl border border-destructive/30 bg-destructive/10 px-3.5 py-2.5 text-[length:var(--text-sm)] text-destructive"
            >
              <WarningCircle aria-hidden size={18} className="shrink-0" />
              {errorMessage}
            </p>
          )}
        </div>

        {consent.modeChoice && mode && (
          <ProBuilderConsentModeChoice
            heading={PRO_BUILDER_CONSENT_MODE_NEXT_TIME}
            value={mode.value}
            onChange={mode.onChange}
            disabled={pending !== null}
            className="mt-4 shrink-0 px-5"
            testId="pro-builder-consent-mode"
          />
        )}

        <div className="mt-4 flex shrink-0 flex-wrap items-center gap-3 border-t border-border bg-muted px-5 py-3.5">
          <span id={noteId} className="inline-flex items-center gap-2 text-[length:var(--text-xs)] text-muted-foreground">
            <PauseCircle aria-hidden size={16} className="shrink-0" />
            {PRO_BUILDER_CONSENT_WAIT_NOTE}
          </span>
          <div className="flex-1" />
          <Button
            type="button"
            size="lg"
            variant="outline"
            data-testid="pro-builder-consent-decline"
            disabled={pending !== null}
            aria-busy={pending === 'decline'}
            onClick={() => onAnswer(false)}
          >
            {pending === 'decline' && <CircleNotch className="animate-spin" aria-hidden />}
            {pending === 'decline' ? PRO_BUILDER_CONSENT_DECLINING : PRO_BUILDER_CONSENT_DECLINE}
          </Button>
          <Button
            type="button"
            size="lg"
            data-testid="pro-builder-consent-approve"
            disabled={pending !== null}
            aria-busy={pending === 'approve'}
            onClick={() => onAnswer(true)}
          >
            {pending === 'approve' ? <CircleNotch className="animate-spin" aria-hidden /> : <Check weight="bold" aria-hidden />}
            {pending === 'approve' ? PRO_BUILDER_CONSENT_APPROVING : PRO_BUILDER_CONSENT_APPROVE}
          </Button>
        </div>
      </section>
    </div>
  );
}

/**
 * The card takes the keyboard when it appears, on the card itself and never on
 * a button, so a keystroke meant for the terminal can never answer it. When it
 * goes and nothing else took focus meanwhile, focus returns to where it was
 * (the terminal). A hidden window cannot take focus; opening it focuses the
 * card through `data-pro-builder-autofocus`.
 */
function useTakeFocus() {
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    const previous = document.activeElement;
    ref.current?.focus({ preventScroll: true });
    return () => {
      const lost = document.activeElement === null || document.activeElement === document.body;
      if (lost && previous instanceof HTMLElement && previous.isConnected) previous.focus({ preventScroll: true });
    };
  }, []);
  return ref;
}

/** Re-renders every second while mounted, for the countdown. */
function useNow(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  return now;
}

/** The answer's state as it concerns THIS card: a failed or pending answer for another card is not this card's. */
export function answerStateFor(
  answer: { isPending: boolean; isError: boolean; error: unknown; variables?: { batchId: string; approved: boolean } },
  batchId: string,
): { pending: 'approve' | 'decline' | null; errorMessage: string | null } {
  const mine = answer.variables?.batchId === batchId;
  return {
    pending: mine && answer.isPending ? (answer.variables?.approved ? 'approve' : 'decline') : null,
    errorMessage: mine && answer.isError ? personFacingMessage(answer.error, PRO_BUILDER_CONSENT_SEND_FAILED) : null,
  };
}

/**
 * The card wired to the poll and the answer mutation; renders nothing while no
 * card waits. Mount it inside the terminal's own box, where the window is open
 * (the poll runs while mounted). A failed answer keeps the card and says so; a
 * 404/409 (gone, or answered elsewhere) ends with the refetch dropping the card.
 */
export function ProBuilderConsentSlot() {
  const consent = useProBuilderConsent(true);
  if (!consent) return null;
  return <WaitingCard key={consent.batchId} consent={consent} />;
}

function WaitingCard({ consent }: { consent: ProBuilderConsent }) {
  const answer = useAnswerProBuilderConsent();
  const now = useNow();
  const status = useProBuilderStatus();
  const [picked, setPicked] = useState<ProBuilderConsentMode | null>(null);
  const { pending, errorMessage } = answerStateFor(answer, consent.batchId);
  return (
    <ProBuilderConsentCard
      consent={consent}
      now={now}
      pending={pending}
      errorMessage={errorMessage}
      mode={{ value: shownConsentMode(picked, status.data), onChange: setPicked }}
      onAnswer={(approved) => answer.mutate({ batchId: consent.batchId, approved, ...answerModeFor(consent, picked, approved) })}
    />
  );
}

/**
 * FRONT-3230: the choice the card shows — the person's pick, else the saved
 * setting (an older server's status has none: ask every time), else nothing
 * while the setting has not loaded, so the card never claims a value nobody saved.
 */
export function shownConsentMode(
  picked: ProBuilderConsentMode | null,
  status: Pick<ProBuilderStatus, 'consentMode'> | undefined,
): ProBuilderConsentMode | undefined {
  if (picked) return picked;
  if (!status) return undefined;
  return status.consentMode ?? 'ask-all';
}

/**
 * FRONT-3230: what the answer carries. Only a choice the person actually
 * picked, and only on a card that offered it: an untouched choice leaves the
 * saved setting alone, even when the card showed it before the setting loaded.
 */
export function answerModeFor(
  consent: Pick<ProBuilderConsent, 'modeChoice'>,
  picked: ProBuilderConsentMode | null,
  approved: boolean,
): { mode?: ProBuilderConsentMode } {
  // The choice rides only with Approve (the server ignores it on Decline anyway): a Decline never changes the setting.
  return approved && consent.modeChoice && picked ? { mode: picked } : {};
}
